<template>
    <div ref="mapElementRef" class="comp-auditoria-map" :style="{ height: props.mapHeight || '100vh' }">
        <MapLayerSwitcher :map="mapRef" default-base-map="osm" v-if="mapRef" />
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import Map from 'ol/Map';
import View from 'ol/View';
import GeoJSON from 'ol/format/GeoJSON';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import { defaults as defaultControls, FullScreen, ScaleLine } from 'ol/control';
import MapLayerSwitcher from '../MapsOpenLayers/Components/MapLayerSwitcher.vue';
import {getMapDefaultCenterView} from "../../../open-layers/map-defaults";

const props = defineProps([
    'posic',
    'zoom_busca',
    'geom_lote',
    'geom_edif',
    'geom_conf',
    'camadas_dinamicas_data1',
    'camadas_dinamicas_data2',
    'mapHeight',
]);

const mapElementRef = ref(null);
const mapRef = ref(null);
const geoJsonFormat = new GeoJSON();

function parseJson(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
}

function parseCenter(value) {
    const center = parseJson(value, getMapDefaultCenterView());
    return Array.isArray(center) && center.length >= 2 ? center : getMapDefaultCenterView();
}

function featureCollection(features) {
    return {
        type: 'FeatureCollection',
        crs: {
            type: 'name',
            properties: {
                name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
            },
        },
        features: parseJson(features, []),
    };
}

function readFeatures(value) {
    const geoJsonValue = parseJson(value, null);

    if (!geoJsonValue) return [];

    if (geoJsonValue.type === 'FeatureCollection' || geoJsonValue.type === 'Feature') {
        return geoJsonFormat.readFeatures(geoJsonValue, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:4326',
        });
    }

    return [
        geoJsonFormat.readFeature(
            {
                type: 'Feature',
                geometry: geoJsonValue,
                properties: {},
            },
            {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:4326',
            }
        ),
    ].filter(Boolean);
}

function rgba(baseColor, alpha) {
    return Array.isArray(baseColor) ? baseColor.concat([alpha]) : baseColor;
}

function createVectorLayer({ sourceData, title, visible = true, displayInLayerSwitcher = true, style, properties = {} }) {
    return new VectorLayer({
        source: new VectorSource({
            features: readFeatures(sourceData),
        }),
        title,
        visible,
        style,
        properties: {
            title,
            name: title,
            displayInLayerSwitcher,
            ...properties,
        },
    });
}

function createLoteStyle(feature) {
    const color = feature.get('color') || [255, 128, 0];

    return new Style({
        text: new Text({
            text: feature.get('medida'),
            font: 'bold 11px sans-serif',
            stroke: new Stroke({ width: 3, color: [255, 255, 255] }),
        }),
        stroke: new Stroke({ width: 4, color }),
        fill: new Fill({ color: rgba(color, 0.3) }),
    });
}

function createQuadraStyle(feature) {
    const strokeColor = feature.get('color') || [0, 255, 255];
    const fillColor = feature.get('color') || [0, 0, 255];

    return new Style({
        text: new Text({
            text: feature.get('medida'),
            font: 'bold 11px sans-serif',
            stroke: new Stroke({ width: 2.5, color: [255, 255, 255] }),
        }),
        stroke: new Stroke({ width: 5.5, color: strokeColor }),
        fill: new Fill({ color: rgba(fillColor, 0.3) }),
    });
}

function createLindeirosStyle(feature) {
    const strokeColor = feature.get('color') || [255, 0, 0];
    const fillColor = feature.get('color') || [255, 128, 0];

    return new Style({
        text: new Text({
            text: feature.get('matricula'),
            font: 'bold 11px sans-serif',
            stroke: new Stroke({ width: 1, color: [255, 255, 255] }),
        }),
        stroke: new Stroke({ width: 4, color: strokeColor }),
        fill: new Fill({ color: rgba(fillColor, 0.3) }),
    });
}

function getRandomColor() {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
}

function createAuditStyle(layerName, color) {
    return new Style({
        stroke: new Stroke({
            color,
            width: 8,
        }),
        fill: new Fill({
            color,
        }),
        text: new Text({
            text: String(layerName).split(': ')[1] || String(layerName),
            font: 'bold 14px sans-serif',
            stroke: new Stroke({ width: 1, color: [55, 255, 255, 0.6] }),
            fill: new Fill({ color: [255, 255, 255, 0.9] }),
        }),
    });
}

function createAuditLayers(layersData = [], tableName = null) {
    return (layersData || []).map((camadaData) => {
        const layerColor = getRandomColor();

        return createVectorLayer({
            sourceData: camadaData.geojson,
            title: camadaData.nome,
            visible: Boolean(camadaData.ativa ?? camadaData.active),
            style: () => createAuditStyle(camadaData.nome, layerColor),
            properties: tableName ? { geo_table: tableName } : {},
        });
    });
}

onMounted(() => {
    const zoom = Number(props.zoom_busca || 13);

    const vectorLayer = createVectorLayer({
        sourceData: featureCollection(props.geom_lote),
        title: 'Lote',
        properties: {
            geo_table: 'public.geo_lote',
        },
        style: createLoteStyle,
    });

    const vectorLayerEdif = createVectorLayer({
        sourceData: featureCollection(props.geom_edif),
        title: 'Quadra',
        properties: {
            geo_table: 'public.geo_quadras',
        },
        style: createQuadraStyle,
    });

    const vectorLayerConf = createVectorLayer({
        sourceData: featureCollection(props.geom_conf),
        title: 'Lindeiros lotes',
        properties: {
            geo_table: 'public.geo_lote',
        },
        style: createLindeirosStyle,
    });

    const auditLoteLayers = createAuditLayers(props.camadas_dinamicas_data1, 'public.geo_lote_audit');
    const auditEdificacaoLayers = createAuditLayers(props.camadas_dinamicas_data2, 'public.geo_edificacao_audit');

    mapRef.value = new Map({
        target: mapElementRef.value,
        layers: [
            ...auditLoteLayers,
            ...auditEdificacaoLayers,
            vectorLayerConf,
            vectorLayerEdif,
            vectorLayer,
        ],
        controls: defaultControls({
            attribution: false,
            attributionOptions: {
                collapsible: false,
            },
        }).extend([
            new ScaleLine(),
            new FullScreen({
                tipLabel: 'Tela Cheia',
            }),
        ]),
        view: new View({
            minZoom: 15,
            maxZoom: 28,
            center: parseCenter(props.posic),
            projection: 'EPSG:4326',
            zoom,
        }),
    });
});

onUnmounted(() => {
    mapRef.value?.setTarget(undefined);
});
</script>

<style scoped>
.comp-auditoria-map {
    width: 100%;
    position: relative;
}
</style>
